/// <amd-module name="DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/AzureRMEndpoint/Components/Generic/GenericAuthEndpointDetailsComponent" />

import * as React from "react";

import * as Resources from "DistributedTaskUI/ServiceEndpoints/Editor/Resources";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import {
    IAzurRMComponentProps,
    IAzurRMComponentState
} from "DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/AzureRMEndpoint/Components/AzureRMAutoServicePrincipalEndpointEditorComponent";
import { EndpointsEditorPanelUtils } from "DistributedTaskUI/ServiceEndpoints/Editor/Utils/EndpointsEditorPanelUtils";
import { GenericAuthorizationComponent } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/GenericEndpointComponents/GenericAuthorizationComponent";
import { GenericEndpointsDetailsComponents } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/GenericEndpointComponents/GenericEndpointsDetailsComponents";
import { ServiceEndpointRequest, ServiceEndpointRequestResult } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";
import { ServiceEndpointsSource } from "DistributedTaskUI/ServiceEndpoints/Editor/Source/ServiceEndpointsSource";

export interface IGenericAuthorizationEndpointDetailsComponentState extends IAzurRMComponentState {
    isVerifyInProgress: boolean;
    isVerificationSuccess: boolean;
}

export class GenericAuthEndpointDetailsComponent extends VssComponent<
    IAzurRMComponentProps,
    IGenericAuthorizationEndpointDetailsComponentState
> {
    constructor(props: IAzurRMComponentProps, context: IVssComponentContext) {
        super(props, context);
        this._source = ServiceEndpointsSource.getInstance(context.pageContext);
    }

    public render(): React.ReactNode {
        return (
            <React.Fragment>
                <div>
                    <GenericAuthorizationComponent
                        disableAllPipelinesSetting={this.props.disableAllPipelinesSetting}
                        serviceEndpointType={this.props.serviceEndpointType}
                        draftEndpoint={this.props.draftEndpoint}
                        isUpdateForm={this.props.isUpdateForm}
                        updateDraftEndpointState={this.props.updateDraftEndpointState}
                        updateInvalidInputsCount={this.props.updateInvalidInputsCount}
                    />
                    {/* This verificationcomponent is being rendered in other endpoint components as well. Ensure to make similar changes at other places if this is updated */}
                    {this._getVerificationComponent()}
                    <GenericEndpointsDetailsComponents
                        disableAllPipelinesSetting={this.props.disableAllPipelinesSetting}
                        serviceEndpoint={this.props.draftEndpoint}
                        isUpdateForm={this.props.isUpdateForm}
                        updateDraftEndpointState={this.props.updateDraftEndpointState}
                        updateInvalidInputsCount={this.props.updateInvalidInputsCount}
                        updateAllowAllPipelinesFlag={this.props.updateAllowAllPipelinesFlag}
                        allowAllPipelinesFlagState={this.props.allowAllPipelinesFlagState}
                    />
                </div>
            </React.Fragment>
        );
    }

    private _getVerificationComponent(): React.ReactNode {
        if (this.props.isVerifiableConnection) {
            const isVerificationDisabled: boolean =
                (this.state && this.state.isVerifyInProgress) ||
                this.props.getInvalidInputCount() > 1 ||
                (this.props.getInvalidInputCount() === 1 && !!this.props.draftEndpoint.name && this.props.draftEndpoint.name.trim().length > 0);
            return (
                <div className={"flex-row default-input-margin-top"}>
                    {EndpointsEditorPanelUtils.getVerifyButton(isVerificationDisabled, this._verifyEndpoint)}
                    {this.state &&
                        EndpointsEditorPanelUtils.getVerificationDetails(
                            this.state.isVerifyInProgress,
                            this.state.isVerificationSuccess,
                            this.props.errorMessage
                        )}
                </div>
            );
        } else {
            return null;
        }
    }

    private _verifyEndpoint = (): void => {
        this._updateVerificationStatus(true, undefined);
        this._verifyConnection()
            .then((serviceEndpointRequestResult: ServiceEndpointRequestResult) => {
                if (serviceEndpointRequestResult.errorMessage) {
                    this._updateVerificationStatus(false, false);
                    this.props.updateError(
                        true,
                        serviceEndpointRequestResult.errorMessage,
                        Resources.VerificationFailed,
                        serviceEndpointRequestResult.activityId
                    );
                } else {
                    this._updateVerificationStatus(false, true);
                    this.props.updateError(false, "");
                }
            })
            .catch((error) => {
                this._updateVerificationStatus(false, false);
                const errorMessage: string = error.message ? error.message : Resources.ValidateEndpointErrorMessage;
                this.props.updateError(true, errorMessage, Resources.VerificationFailed);
            });
    }

    private _updateVerificationStatus = (isVerifyInProgress: boolean, isVerificationSuccess: boolean): void => {
        this.setState({
            isVerifyInProgress: isVerifyInProgress,
            isVerificationSuccess: isVerificationSuccess
        });
    }

    private _verifyConnection(): Promise<ServiceEndpointRequestResult> {
        const serviceEndpointRequest: ServiceEndpointRequest = EndpointsEditorPanelUtils.getServiceEndpointRequestForTest(this.props.draftEndpoint);
        return this._source.verifyServiceEndpoint(serviceEndpointRequest, this.props.draftEndpoint.id);
    }

    private _source: ServiceEndpointsSource;
}
